import { useState, useEffect, useCallback } from 'react';
import { DashboardData } from '../types/dashboard';
import { DashboardService } from '../services/dashboard-service';

const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes in milliseconds

export const useDashboardData = () => {
  const [data, setData] = useState<DashboardData>({
    stats: {
      totalDocuments: 0,
      totalCollections: 0,
      totalUsers: 0,
    },
    isLoading: true,
    error: null,
    lastUpdated: null,
  });

  const fetchData = useCallback(async () => {
    try {
      setData(prev => ({ ...prev, isLoading: true, error: null }));
      
      const stats = await DashboardService.getDashboardStats();
      
      setData({
        stats,
        isLoading: false,
        error: null,
        lastUpdated: new Date(),
      });
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      setData(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch dashboard data',
      }));
    }
  }, []);

  const refresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    // Initial load
    fetchData();

    // Set up periodic refresh
    const interval = setInterval(fetchData, REFRESH_INTERVAL);

    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, [fetchData]);

  return {
    ...data,
    refresh,
  };
};

export default useDashboardData;
